/**
 * 社区帖子列表页面
 * 显示历史帖子列表，支持查看详情
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, message } from 'antd';
import { MessageOutlined } from '@ant-design/icons';
import { PageHeader } from '../../components/Layout/PageHeader';
import { communityPostService, CommunityPostItem, CommunityPostDetail } from '../../services/dashboardService';
import './CommunityPosts.css';

// Components
import { SearchFilterBar } from './components/SearchFilterBar';
import { PostListTable } from './components/PostListTable';
import { PostDetailModal } from './components/PostDetailModal';
import { ScrapeStatsCard } from './components/ScrapeStatsCard';

const CommunityPostsList: React.FC = () => {
    const [posts, setPosts] = useState<CommunityPostItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [sortBy, setSortBy] = useState('reply_count');
    const [sortOrder, setSortOrder] = useState('desc');

    // 抓取进度
    const [scrapeStats, setScrapeStats] = useState<{ total: number; scraped: number; pending: number; failed: number } | null>(null);
    const [bulkScraping, setBulkScraping] = useState(false);

    // 详情模态框
    const [detailModalVisible, setDetailModalVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [currentPost, setCurrentPost] = useState<CommunityPostDetail | null>(null);

    // 加载抓取状态
    const loadScrapeStats = useCallback(async () => {
        try {
            const response = await communityPostService.getScrapeStatus();
            if (response.success) setScrapeStats(response.stats);
        } catch (error) { console.error('Load scrape stats error:', error); }
    }, []);

    // 加载帖子列表
    const loadPosts = useCallback(async () => {
        setLoading(true);
        try {
            const response = await communityPostService.getPosts(page, pageSize, statusFilter, searchKeyword, sortBy, sortOrder);
            if (response.success) {
                setPosts(response.data);
                setTotal(response.total);
            }
        } catch (error) { message.error('加载帖子列表失败'); }
        finally { setLoading(false); }
    }, [page, pageSize, statusFilter, searchKeyword, sortBy, sortOrder]);

    useEffect(() => { loadPosts(); loadScrapeStats(); }, [loadPosts, loadScrapeStats]);

    // Explicit trigger when filters change
    useEffect(() => {
        loadPosts();
    }, [statusFilter, searchKeyword]);

    // 定时刷新抓取状态
    useEffect(() => {
        const interval = setInterval(loadScrapeStats, 5000);
        return () => clearInterval(interval);
    }, [loadScrapeStats]);

    // 刷新帖子（从网站重新抓取）
    const handleRefresh = async () => {
        setRefreshing(true);
        try {
            const result = await communityPostService.refreshPosts(false);
            if (result.success) {
                message.success(`刷新成功！新增 ${result.saved} 条，更新 ${result.updated} 条`);
                loadPosts();
            }
        } catch (error) { message.error('刷新失败'); }
        finally { setRefreshing(false); }
    };

    // 批量抓取 / 重置
    const handleBulkScrape = async (force: boolean = false) => {
        setBulkScraping(true);
        try {
            const result = await communityPostService.startBulkScrape(5, force);
            if (result.success) {
                message.success(result.message);
                loadScrapeStats();
            } else { message.error(result.error || '启动失败'); }
        } catch (e) { message.error('请求失败'); }
        finally { setBulkScraping(false); }
    };

    // 查看详情
    const handleViewDetail = async (post: CommunityPostItem) => {
        setDetailModalVisible(true);
        setDetailLoading(true);
        setCurrentPost(null);
        try {
            const response = await communityPostService.getPostDetail(post.id);
            if (response.success) setCurrentPost(response.data);
            else message.error('加载详情失败');
        } catch (error) { message.error('加载详情失败'); }
        finally { setDetailLoading(false); }
    };

    // 手动抓取详情
    const handleScrapeDetail = async (postId: number) => {
        try {
            const result = await communityPostService.scrapePostDetail(postId);
            if (result.success) {
                message.success('抓取成功');
                loadPosts();
            } else { message.error(result.error || '抓取失败'); }
        } catch (error) { message.error('抓取失败'); }
    };

    return (
        <div className="community-posts-page">
            <PageHeader
                title="社区热帖"
                subtitle="跨境电商社区热门帖子存档，保留原始内容"
                icon={<MessageOutlined />}
            />

            <Card className="community-posts-card">
                <ScrapeStatsCard stats={scrapeStats} />
                <SearchFilterBar
                    searchKeyword={searchKeyword}
                    onSearchChange={setSearchKeyword}
                    onSearch={() => { setPage(1); loadPosts(); }}
                    sortBy={sortBy}
                    sortOrder={sortOrder}
                    onSortChange={(by, order) => { setSortBy(by); setSortOrder(order); setPage(1); }}
                    statusFilter={statusFilter}
                    onStatusChange={(val) => { setStatusFilter(val); setPage(1); }}
                    bulkScraping={bulkScraping}
                    onBulkScrape={handleBulkScrape}
                    refreshing={refreshing}
                    onRefresh={handleRefresh}
                />
                <PostListTable
                    posts={posts}
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    loading={loading}
                    onPageChange={(p, ps) => { setPage(p); setPageSize(ps); }}
                    onViewDetail={handleViewDetail}
                    onScrapeDetail={handleScrapeDetail}
                />
            </Card>

            <PostDetailModal
                visible={detailModalVisible}
                onCancel={() => setDetailModalVisible(false)}
                loading={detailLoading}
                post={currentPost}
                onScrape={handleScrapeDetail}
            />
        </div>
    );
};

export default CommunityPostsList;
